/**
 * Card Size Picker Popup
 * Multi-variant product cards: clicking the "+" opens a small popup with the
 * per-size reactive list (cloned from the card's <template data-card-size-list>).
 * Self-contained + globally loaded so it can't be affected by other card scripts.
 */
(function () {
  'use strict';
  if (window.SuveeCardSizePopupInit) return;
  window.SuveeCardSizePopupInit = true;

  var popup = null;
  var backdrop = null;
  var currentTrigger = null;
  var lastCart = null;

  // On mobile the popup opens as a sticky bottom sheet (like the drawer variant sheet)
  // instead of a small card-anchored popup.
  function isSheet() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function buildPopup() {
    if (popup) return popup;
    popup = document.createElement('div');
    popup.className = 'card-size-popup';
    popup.setAttribute('role', 'dialog');
    popup.hidden = true;
    document.body.appendChild(popup);
    return popup;
  }

  function buildBackdrop() {
    if (backdrop) return backdrop;
    backdrop = document.createElement('div');
    backdrop.className = 'card-size-popup-backdrop';
    document.body.appendChild(backdrop);
    return backdrop;
  }

  function closePopup() {
    if (!popup || popup.hidden) return;
    popup.hidden = true;
    popup.innerHTML = '';
    popup.classList.remove('card-size-popup--visible', 'card-size-popup--sheet', 'card-size-popup--closing');
    popup.style.left = '';
    popup.style.top = '';
    popup.style.width = '';
    if (backdrop) backdrop.classList.remove('is-open');
    if (currentTrigger) currentTrigger.setAttribute('aria-expanded', 'false');
    currentTrigger = null;
  }

  function position(trigger) {
    var r = trigger.getBoundingClientRect();
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var width = Math.max(300, Math.min(340, vw - 24));
    popup.style.width = width + 'px';
    var h = popup.offsetHeight;
    // Center the popup horizontally on the "+" trigger (clamped to viewport).
    var center = r.left + r.width / 2;
    var left = Math.min(Math.max(center - width / 2, 12), vw - width - 12);
    var top = r.bottom + 8;
    if (top + h > vh - 12) top = Math.max(12, r.top - h - 8);
    popup.style.left = left + 'px';
    popup.style.top = top + 'px';
  }

  // Map of variant_id -> quantity from a cart object.
  function variantQtyMap(cart) {
    var map = {};
    if (cart && cart.items) {
      cart.items.forEach(function (it) {
        var vid = String(it.variant_id);
        map[vid] = (map[vid] || 0) + it.quantity;
      });
    }
    return map;
  }

  // The popup is cloned from a page-load <template>, so its steppers hold stale
  // counts. Hydrate each one from the live cart so reopening shows real quantities.
  function hydratePopup(cart) {
    if (!popup || popup.hidden) return;
    var map = variantQtyMap(cart);
    popup.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach(function (input) {
      var q = map[input.getAttribute('data-quantity-variant-id')] || 0;
      input.value = q;
      input.setAttribute('value', q);
      input.dataset.cartQuantity = q;
      var container = input.closest('.quantity.cart-quantity');
      if (container) container.classList.toggle('quantity--has-items', q > 0);
    });
  }

  function openFor(trigger) {
    var handle = trigger.getAttribute('data-product-handle');
    if (!handle) return;
    var tpl = document.querySelector('template[data-card-size-list="' + handle + '"]');
    if (!tpl || !tpl.content) return;

    buildPopup();
    popup.innerHTML = '';
    popup.appendChild(tpl.content.cloneNode(true));
    popup.hidden = false;
    // reflect current cart quantities on the cloned steppers (instant from cache,
    // then corrected by a fresh fetch below)
    if (lastCart) hydratePopup(lastCart);
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { lastCart = cart; hydratePopup(cart); })
      .catch(function () {});

    var sheet = isSheet();
    if (sheet) {
      popup.classList.add('card-size-popup--sheet');
      buildBackdrop();
    } else {
      popup.classList.remove('card-size-popup--sheet');
    }

    requestAnimationFrame(function () {
      if (sheet) {
        backdrop.classList.add('is-open');
      } else {
        position(trigger);
      }
      popup.classList.add('card-size-popup--visible');
    });
    currentTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
  }

  // Capture phase so it runs before the card's drawer-trigger / link handlers.
  document.addEventListener(
    'click',
    function (e) {
      var trigger = e.target.closest && e.target.closest('[data-card-size-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (currentTrigger === trigger) closePopup();
        else { closePopup(); openFor(trigger); }
        return;
      }
      // Inside the popup: clicking a size "+" adds it — hide the popup INSTANTLY
      // (don't wait for the debounced add + cart request). The add still completes
      // in the background and closePopup() does the full cleanup on cartUpdate.
      if (popup && !popup.hidden && popup.contains(e.target)) {
        if (e.target.closest('.quantity__button[name="plus"]')) {
          popup.classList.add('card-size-popup--closing');
          if (backdrop) backdrop.classList.remove('is-open');
        }
        return;
      }

      // outside click closes
      if (popup && !popup.hidden) closePopup();
    },
    true
  );

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePopup();
  });

  // Close on page scroll/resize, but ignore scrolling inside the popup itself.
  // The bottom-sheet (mobile) stays open while the page scrolls behind it.
  window.addEventListener(
    'scroll',
    function (e) {
      if (!popup || popup.hidden) return;
      if (popup.contains(e.target)) return;
      if (popup.classList.contains('card-size-popup--sheet')) return;
      closePopup();
    },
    true
  );
  window.addEventListener('resize', closePopup);

  /* ---------- Total-across-sizes count on the trigger button ---------- */
  function applyTotals(cart) {
    if (!cart || !cart.items) return;
    lastCart = cart;
    var totals = {};
    cart.items.forEach(function (it) {
      var pid = String(it.product_id);
      totals[pid] = (totals[pid] || 0) + it.quantity;
    });
    document.querySelectorAll('.card__add-btn[data-product-id]').forEach(function (btn) {
      var q = totals[btn.getAttribute('data-product-id')] || 0;
      var countEl = btn.querySelector('.card__add-btn-count');
      if (q > 0) {
        btn.classList.add('card__add-btn--has-items');
        if (countEl) { countEl.textContent = q; countEl.hidden = false; }
      } else {
        btn.classList.remove('card__add-btn--has-items');
        if (countEl) { countEl.hidden = true; }
      }
    });
  }

  function refreshTotals() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(applyTotals)
      .catch(function () {});
  }

  refreshTotals();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.cartData && event.cartData.items) applyTotals(event.cartData);
      else refreshTotals();
      // Adding a size from the popup updates the cart → close the popup.
      closePopup();
    });
  }
  document.addEventListener('cart:refresh', refreshTotals);
})();
